import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, ChevronDown, Eye, Loader2, Trash2 } from 'lucide-react';

import { useSandboxArchive } from 'features/views/hooks/useOntologyViews';
import { useSandboxSession } from '../SandboxSessionContext';
import { publicationError } from '../../approvals/publicationError';
import {
  formatRelativeDraftTime,
  getDraftChangeTags,
  getDraftFreshnessNotice,
  getDraftTitle,
  selectActiveDrafts,
} from '../utils/draftPreviewPresentation';

/** Proposed changes available for review from the agent chat header. */
const DraftPreviewControl = () => {
  const {
    previewSandboxId,
    selectedSandbox,
    isPreviewActive,
    sandboxes,
    isLoadingSandboxes,
    isFetchingSandboxes,
    sandboxesError,
    refetchSandboxes,
    selectPreviewSandbox,
    clearPreview,
    setDraftPickerOpen,
  } = useSandboxSession();
  const deleteSandbox = useSandboxArchive();

  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const activeDrafts = useMemo(() => sandboxesError ? [] : selectActiveDrafts(sandboxes), [sandboxes, sandboxesError]);
  // Background eligibility polls must preserve the control, including an empty queue.
  const isLoadingDraftOptions = isLoadingSandboxes;

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, 260);
    const left = Math.min(rect.left, window.innerWidth - menuWidth - 8);
    setMenuStyle({ position: 'fixed', top: rect.bottom + 6, left: Math.max(8, left), width: menuWidth });
  }, [isOpen]);

  // An open picker lists the branches as they are now and keeps them current.
  useEffect(() => {
    if (!isOpen) return undefined;
    setDraftPickerOpen?.(true);
    void refetchSandboxes?.();
    return () => setDraftPickerOpen?.(false);
  }, [isOpen, setDraftPickerOpen, refetchSandboxes]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      const inTrigger = triggerRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) setIsOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const loadError = sandboxesError ? (
    <span role="alert" className="flex items-center gap-2 text-xs text-rose-700">
      Could not load proposed changes.
      <button type="button" onClick={refetchSandboxes} disabled={isFetchingSandboxes}
        className="font-semibold underline">Retry changes</button>
    </span>
  ) : null;

  // Only proposed changes to review (or one being previewed) earn a place in the chat
  // header; with none, the chat stays a conversation. A failed load still says so.
  if (!isPreviewActive && !activeDrafts.length) return loadError;

  const previewTitle = selectedSandbox ? getDraftTitle(selectedSandbox) : 'Proposal';
  const freshnessNotice = isPreviewActive ? getDraftFreshnessNotice(selectedSandbox) : '';

  const handleGoLive = () => {
    clearPreview();
    setIsOpen(false);
  };

  const handleSelectDraft = (sandboxId) => {
    selectPreviewSandbox(sandboxId);
    setIsOpen(false);
  };

  const handleDeleteDraft = async (event, sandbox) => {
    event.preventDefault();
    event.stopPropagation();
    const title = getDraftTitle(sandbox);
    const confirmed = window.confirm(`Discard "${title}"? It will leave the proposed changes list. It will no longer be available to preview.`);
    if (!confirmed) return;
    try {
      await deleteSandbox.mutateAsync({ sandboxId: sandbox.sandbox_id });
      if (sandbox.sandbox_id === previewSandboxId) {
        clearPreview();
      }
    } catch (error) {
      window.alert(publicationError(error?.message) || 'Could not discard this proposal.');
    }
  };

  return (
    <>
      {loadError}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm transition ${
          isPreviewActive
            ? 'border-indigo-500 bg-indigo-600 text-white hover:bg-indigo-700'
            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
        }`}
        title={isPreviewActive ? `Previewing changes: ${previewTitle}` : 'Review proposed changes'}
      >
        {isPreviewActive ? (
          <>
            <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="shrink-0 text-indigo-200">Preview</span>
            <span className="min-w-0 truncate">{previewTitle}</span>
          </>
        ) : (
          <>
            <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
            <span className="min-w-0 truncate">Proposed changes</span>
          </>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${
            isPreviewActive ? 'text-indigo-200' : 'text-gray-400'
          }`}
          aria-hidden
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            role="listbox"
            aria-label="Viewing"
            className="z-[9999] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5"
          >
            <div className="border-b border-gray-100 px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Viewing</div>
            </div>
            <div className="max-h-72 overflow-y-auto p-1.5">
              <button
                type="button"
                role="option"
                aria-selected={!isPreviewActive}
                onClick={handleGoLive}
                className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition ${
                  !isPreviewActive ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200' : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium leading-tight">Live</span>
                  <span className="block truncate text-[11px] leading-snug text-gray-500">
                    Your current workspace
                  </span>
                </span>
                {!isPreviewActive ? <Check className="h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.5} aria-hidden /> : null}
              </button>

              {activeDrafts.length > 0 ? (
                <div className="px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Proposed changes
                </div>
              ) : null}

              {activeDrafts.map((sandbox) => {
                const isSelected = sandbox.sandbox_id === previewSandboxId;
                const tags = getDraftChangeTags(sandbox).slice(0, 2);
                const relativeTime = formatRelativeDraftTime(sandbox?.updated_at || sandbox?.created_at);
                const secondary = [sandbox.preview_access === 'chat' ? 'Working in this chat' : 'Awaiting your approval', tags.map((tag) => tag.label).join(' · '), relativeTime].filter(Boolean).join(' · ');
                const draftFreshness = getDraftFreshnessNotice(sandbox);
                const isDeleting = deleteSandbox.isPending && deleteSandbox.variables?.sandboxId === sandbox.sandbox_id;
                return (
                  <div
                    key={sandbox.sandbox_id}
                    className={`flex w-full items-start gap-1 rounded-lg ${
                      isSelected ? 'bg-indigo-50 text-indigo-900 ring-1 ring-indigo-200' : 'text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelectDraft(sandbox.sandbox_id)}
                      className="flex min-w-0 flex-1 items-start gap-2.5 px-2.5 py-2 text-left"
                    >
                      <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-500" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium leading-tight">{getDraftTitle(sandbox)}</span>
                        {secondary ? (
                          <span className="mt-0.5 block truncate text-[11px] leading-snug text-gray-500">{secondary}</span>
                        ) : null}
                        {draftFreshness ? (
                          <span className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-800">
                            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                            <span className="line-clamp-1">{draftFreshness}</span>
                          </span>
                        ) : null}
                      </span>
                      {isSelected ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" strokeWidth={2.5} aria-hidden /> : null}
                    </button>
                    {sandbox.can_discard ? (
                    <button
                      type="button"
                      title="Discard proposal"
                      aria-label="Discard proposal"
                      disabled={isDeleting}
                      onClick={(event) => handleDeleteDraft(event, sandbox)}
                      className="mr-1.5 mt-1.5 shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    >
                      {isDeleting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                    ) : null}
                  </div>
                );
              })}
              {!isPreviewActive && !activeDrafts.length && !isLoadingDraftOptions && !sandboxesError ? (
                <p className="px-2.5 py-3 text-xs text-gray-500">No proposed changes. You are viewing the live workspace.</p>
              ) : null}
            </div>
            {freshnessNotice ? (
              <div className="flex items-center gap-1.5 border-t border-amber-100 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                {freshnessNotice}
              </div>
            ) : null}
          </div>,
          document.body
        )}
    </>
  );
};

export default DraftPreviewControl;
